// Source pane — CodeMirror + its pane header. Stateless wrapper that
// hoists the EditorView reference up to the parent via setView.
//
// Memoized because the parent (App) re-renders at the hap-poll rate
// (~12fps) during playback. Without memo, CodeMirror's @uiw wrapper
// reconciles 12 times a second even when code hasn't changed. App
// now passes a stable `setView` via useCallback, so the memo bails
// cleanly when only frame data changes.

import { memo, useMemo } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { EditorView, placeholder } from '@codemirror/view';
import { strudelTheme } from './codemirror/strudelTheme';
import { voiceColorsExtension } from './codemirror/voiceColorsExtension';
import { playingLinesExtension } from './codemirror/playingLinesExtension';
import { strudelCompletions } from './codemirror/strudelCompletions';
import { playableSoloGutter } from './codemirror/playableSoloGutter';
import { changeFlashExtension } from './codemirror/changeFlashExtension';
import { firstEditTargetExtension } from '../onboarding/firstEditTarget';

export type EditorSelection = {
  /** Selected text exactly as it appears in the buffer. */
  text: string;
  /** 1-based inclusive line range. */
  fromLine: number;
  toLine: number;
};

type Props = {
  code: string;
  onChange: (next: string) => void;
  setView: (view: EditorView | null) => void;
  /** Called whenever the selection changes to a non-empty range, and
   *  once with null when the selection collapses back to a cursor. */
  onSelectionChange?: (selection: EditorSelection | null) => void;
};

const BASE_EXTENSIONS = [
  javascript({ jsx: false }),
  placeholder('Strudel code…'),
  EditorView.lineWrapping,
  voiceColorsExtension,
  playingLinesExtension,
  playableSoloGutter,
  strudelCompletions,
  changeFlashExtension,
  // Inert until the onboarding tour arms it via setFirstEditTarget —
  // registering it statically keeps BASE_EXTENSIONS stable.
  firstEditTargetExtension,
];

// Build an EditorView.updateListener that emits selection changes.
// Fires exactly once per selection mutation (not per cursor blink).
// Skipped entirely when no callback is provided — no allocation cost.
function selectionListener(
  onSelectionChange: (s: EditorSelection | null) => void,
) {
  let lastFrom = -1;
  let lastTo = -1;
  return EditorView.updateListener.of((update) => {
    if (!update.selectionSet && !update.docChanged) return;
    const { main } = update.state.selection;
    if (main.from === lastFrom && main.to === lastTo) return;
    lastFrom = main.from;
    lastTo = main.to;
    if (main.from === main.to) {
      onSelectionChange(null);
      return;
    }
    const doc = update.state.doc;
    const fromLine = doc.lineAt(main.from).number;
    const toLine = doc.lineAt(main.to).number;
    onSelectionChange({
      text: doc.sliceString(main.from, main.to),
      fromLine,
      toLine,
    });
  });
}

const BASIC_SETUP = {
  lineNumbers: true,
  foldGutter: false,
  highlightActiveLine: false,
  highlightActiveLineGutter: false,
  autocompletion: false, // strudelCompletions extension supplies a context-aware source
  bracketMatching: true,
  closeBrackets: true,
  indentOnInput: true,
  history: true,
  searchKeymap: true,
  defaultKeymap: true,
  dropCursor: true,
  rectangularSelection: false,
  crosshairCursor: false,
  allowMultipleSelections: false,
  highlightSelectionMatches: false,
  tabSize: 2,
};

export const SourcePane = memo(SourcePaneInner);

function SourcePaneInner({
  code,
  onChange,
  setView,
  onSelectionChange,
}: Props) {
  const extensions = useMemo(
    () =>
      onSelectionChange
        ? [...BASE_EXTENSIONS, selectionListener(onSelectionChange)]
        : BASE_EXTENSIONS,
    [onSelectionChange],
  );

  return (
    <div className="flex flex-col h-full min-h-0 bg-zinc-950">
      <header className="flex items-center px-4 py-2 border-b border-zinc-900 bg-zinc-900/60">
        <span className="text-[9px] tracking-[0.32em] uppercase text-zinc-500 font-display">
          source
        </span>
      </header>
      <div
        className="flex-1 min-h-0 overflow-hidden"
        aria-label="Strudel code editor"
      >
        <CodeMirror
          value={code}
          onChange={onChange}
          theme={strudelTheme}
          extensions={extensions}
          basicSetup={BASIC_SETUP}
          indentWithTab
          height="100%"
          style={{ height: '100%' }}
          onCreateEditor={(view) => setView(view)}
        />
      </div>
    </div>
  );
}
